import KEY_CODE from './KeyCode'

// Source: https://www.w3.org/TR/wai-aria-practices-1.1/examples/dialog-modal/alertdialog.html

const DIALOG_OPEN_CLASS = 'has-dialog'

let ignoreUtilFocusChanges = false

const isFocusable = element => {
  if (
    element.tabIndex > 0 ||
    (element.tabIndex === 0 && element.getAttribute('tabIndex') !== null)
  ) {
    return true
  }

  if (element.disabled) {
    return false
  }

  switch (element.nodeName) {
    case 'A':
      return !!element.href && element.rel != 'ignore'
    case 'INPUT':
      return element.type != 'hidden' && element.type != 'file'
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true
    default:
      return false
  }
}

const remove = item => {
  if (item.remove && typeof item.remove === 'function') {
    return item.remove()
  }
  if (
    item.parentNode &&
    item.parentNode.removeChild &&
    typeof item.parentNode.removeChild === 'function'
  ) {
    return item.parentNode.removeChild(item)
  }
  return false
}

const Modal = element => {
  return class extends element {
    openDialog() {
      this.focusAfterClosed = this

      this.backdrop && this.backdrop.classList.add('active')
      document.documentElement.classList.add(DIALOG_OPEN_CLASS)
      this.focusAfterClosed = this.openButton

      // Bracket the dialog node with two invisible, focusable nodes.
      // While this dialog is open, we use these to make sure that focus never
      // leaves the document even if dialogNode is the first or last node.
      const preDiv = document.createElement('div')
      const postDiv = document.createElement('div')
      this.pre = this.dialog.parentNode.insertBefore(preDiv, this.dialog)
      this.pre.tabIndex = 0
      this.post = this.dialog.parentNode.insertBefore(
        postDiv,
        this.dialog.nextSibling
      )
      this.post.tabIndex = 0

      this.dialog.className = 'default_dialog'
      this.focusFirstDescendant(this.dialog, true)
      this.addListeners()
      this.lastFocus = document.activeElement
    }

    closeDialog() {
      remove(this.pre)
      remove(this.post)

      this.dialog.className = 'hidden'
      this.backdrop.classList.remove('active')
      document.documentElement.classList.remove(DIALOG_OPEN_CLASS)
      this.focusAfterClosed.focus()
      this.removeListeners()

      return true
    }

    isDialog() {
      const validRoles = ['dialog', 'alertdialog']

      const isDialog = (this.dialog.getAttribute('role') || '')
        .trim()
        .split(/\s+/g)
        .some(function(token) {
          return validRoles.some(function(role) {
            return token === role
          })
        })

      if (!isDialog) {
        throw new Error(
          'Dialog() requires a DOM element with ARIA role of dialog or alertdialog.'
        )
      }
    }

    attemptFocus(element, preventScroll = false) {
      if (!isFocusable(element)) {
        return false
      }

      ignoreUtilFocusChanges = true

      try {
        element.focus({ preventScroll })
      } catch (e) {
        // ...
      }
      ignoreUtilFocusChanges = false

      return document.activeElement === element
    }

    focusFirstDescendant(element, preventScroll = false) {
      let child

      for (let i = 0; i < element.childNodes.length; i++) {
        child = element.childNodes[i]

        if (
          this.attemptFocus(child, preventScroll) ||
          this.focusFirstDescendant(child, preventScroll)
        ) {
          return true
        }
      }
      return false
    }

    focusLastDescendant(element) {
      let child

      for (let i = element.childNodes.length - 1; i >= 0; i--) {
        child = element.childNodes[i]
        if (this.attemptFocus(child) || this.focusLastDescendant(child)) {
          return true
        }
      }
      return false
    }

    trapFocus(event) {
      if (ignoreUtilFocusChanges) {
        return
      }

      if (this.dialog.contains(event.target)) {
        this.lastFocus = event.target
      } else {
        this.focusFirstDescendant(this.dialog)
        if (this.lastFocus == document.activeElement) {
          this.focusLastDescendant(this.dialog)
        }
        this.lastFocus = document.activeElement
      }
    }

    handleEscape(event) {
      var key = event.which || event.keyCode

      if (key === KEY_CODE.ESC && this.closeDialog()) {
        event.stopPropagation()
      }
    }

    handleBackdropClick(event) {
      if (event.target === this.backdrop) {
        this.closeDialog()
      }
    }

    get dialog() {
      return this.child('dialog')
    }

    get backdrop() {
      return (
        this.child('backdrop') || this.querySelector('[data-modal-backdrop]')
      )
    }

    get openButton() {
      return this.child('open')
    }

    get closeButton() {
      return this.child('close')
    }

    addListeners() {
      document.addEventListener('keyup', this.handleEscape)
      document.addEventListener('focus', this.trapFocus, true)
    }

    removeListeners() {
      document.removeEventListener('focus', this.trapFocus, true)
      document.removeEventListener('keyup', this.handleEscape)
    }

    init() {
      this.openDialog = this.openDialog.bind(this)
      this.closeDialog = this.closeDialog.bind(this)
      this.trapFocus = this.trapFocus.bind(this)
      this.handleEscape = this.handleEscape.bind(this)
      this.handleBackdropClick = this.handleBackdropClick.bind(this)

      this.isDialog()

      this.closeButton &&
        this.closeButton.addEventListener('click', this.closeDialog)
      this.openButton &&
        this.openButton.addEventListener('click', this.openDialog)
      this.backdrop &&
        this.backdrop.addEventListener('click', this.handleBackdropClick)
    }
  }
}

export default Modal
